import React, { useContext } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Checkbox, Spin } from "antd";
import { motion } from "framer-motion/dist/framer-motion";
import moment from "moment";
import Highlighter from "react-highlight-words";

import LockedValue from "../../../grace-period/components/LockedValue";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import { getReportAlertRemainingDisplay } from "./alertRemainingUtils";
import { ReportsTableRowContext } from "./ReportsTableRow";

import { ReactComponent as ExpiryFlag } from "../../../../assets/images/sample-submission/ExpiryFlag.svg";

import "./ReportsTableFieldCell.css";

const ALERT_FLAG_ANIMATION = {
  initial: {
    bottom: 2,
    scale: 0.22,
    opacity: 1,
  },
  animate: {
    bottom: [2, 5, 5, 2],
    scale: [0.22, 1, 1, 0.22],
    opacity: [1, 1, 1, 0],
  },
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeOut",
    times: [0, 0.1, 0.82, 1],
  },
  style: {
    left: "50%",
    x: "-50%",
    transformOrigin: "50% 100%",
  },
};

const isItemColumn = (jsonField) => (
  String(jsonField || "").toLowerCase() === "item"
);

export default function ReportsTableFieldCell({
  rowData,
  jsonField,
  searchValToHighlight,
  showCheckbox,
  reserveAlertFlagSlot = false,
}) {
  const { areResultsLocked } = useAccessStatus();
  const { onRowSelect, checkboxLoading } = useContext(ReportsTableRowContext);
  /**
   * Get value for the cell.
   * @param {Object} item row data
   * @param {String} jsonField
   * @returns {String}
   */
  const getJsonFieldDataForDisplay = (val) => {
    if (jsonField === "received_date") {
      return moment(new Date(val)).format("MM/DD/YYYY");
    }

    if (val) {
      return val;
    }

    return "";
  };

  const isEmpty = rowData.getValue() === undefined || rowData.getValue().trim() === "";
  const showLockedCheckbox = showCheckbox && areResultsLocked;
  const enableRowCheckBox = showCheckbox && rowData.row.original.specs_flag !== "0" && !areResultsLocked;
  const showAlertFlagSlot = isItemColumn(jsonField) && reserveAlertFlagSlot;
  const showAlertFlag = showAlertFlagSlot
    && Boolean(getReportAlertRemainingDisplay(rowData.row.original));

  const fieldValue = isEmpty ? (
    <div className="ReportsTable__EmptyCell__Content">
      <div className="ReportsTable__EmptyCell__Values">
        {" "}
      </div>
    </div>
  ) : (
    <Highlighter
      highlightClassName="searchHighlight"
      searchWords={[searchValToHighlight]}
      autoEscape
      textToHighlight={getJsonFieldDataForDisplay(rowData.getValue())}
    />
  );
  return (
    <div className={`ReportsTable__FieldCellContent${showCheckbox ? "--show-checkbox" : ""}${showAlertFlagSlot ? " ReportsTable__FieldCellContent--alert-flag" : ""}`}>
      {showLockedCheckbox && (
        <span
          className="ReportsTable__CheckboxLock"
          onClick={(e) => { e.stopPropagation(); }}
        >
          <LockedValue locked blur={false} />
        </span>
      )}
      {showCheckbox && !showLockedCheckbox && (
        <>
          {checkboxLoading && (<Spin indicator={<LoadingOutlined className="ReportsTable__Checkbox__Spin" spin />} />)}
          {!checkboxLoading && (
          <Checkbox
            className={`ReportsTable__Checkbox${enableRowCheckBox ? "--enabled" : "--disabled"}`}
            onChange={enableRowCheckBox ? onRowSelect : null}
            onClick={(e) => { e.stopPropagation(); }}
            checked={rowData.row.getIsSelected()}
          />
          )}
        </>
      )}
      {showAlertFlag && (
        <div
          className="ReportsTable__AlertFlagCell"
          aria-label="Alert remaining warning"
        >
          <motion.div
            className="ReportsTable__AlertFlag"
            initial={ALERT_FLAG_ANIMATION.initial}
            animate={ALERT_FLAG_ANIMATION.animate}
            transition={ALERT_FLAG_ANIMATION.transition}
            style={ALERT_FLAG_ANIMATION.style}
          >
            <ExpiryFlag aria-hidden />
          </motion.div>
        </div>
      )}
      {showAlertFlagSlot && !showAlertFlag && (
        <span className="ReportsTable__AlertFlagPlaceholder" aria-hidden />
      )}
      {fieldValue}
    </div>
  );
}
